import { Injectable } from '@angular/core';
import {
  ActivatedRouteSnapshot,
  CanActivate,
  CanActivateChild,
  CanLoad,
  Route,
  Router,
  RouterStateSnapshot,
  UrlSegment,
  UrlTree,
} from '@angular/router';
import { Store } from '@ngxs/store';
import {
  AccessRightLevelEnum,
  CORE_MODULE_ID,
  EntitlementMode,
} from '@osos/shared/types';
import { Observable } from 'rxjs';
import { map, mapTo, switchMap } from 'rxjs/operators';
import { AccessPermissionService } from '../services/access-permission-service/access-permission.service';
import { PermissionActions } from '../state/permissions/permissions.actions';

@Injectable()
export class AccessPermissionGuard
  implements CanActivate, CanActivateChild, CanLoad
{
  constructor(
    private accessHelper: AccessPermissionService,
    private store: Store,
    private router: Router,
  ) {}

  canActivate(
    route: ActivatedRouteSnapshot,
    state: RouterStateSnapshot,
  ):
    | Observable<boolean | UrlTree>
    | Promise<boolean | UrlTree>
    | boolean
    | UrlTree {
    const moduleId = this._getModuleIdFromRoute(route);
    const entityId = this._getEntityIdFromRoute(route);
    return this._canAccess(route, moduleId, entityId);
  }

  canActivateChild(
    route: ActivatedRouteSnapshot,
    state: RouterStateSnapshot,
  ):
    | Observable<boolean | UrlTree>
    | Promise<boolean | UrlTree>
    | boolean
    | UrlTree {
    const moduleId = this._getModuleIdFromRoute(route);
    const entityId = this._getEntityIdFromRoute(route);
    return this._canAccess(route, moduleId, entityId);
  }

  canLoad(
    route: Route,
    segments: UrlSegment[],
  ):
    | Observable<boolean | UrlTree>
    | Promise<boolean | UrlTree>
    | boolean
    | UrlTree {
    return this._canAccess(route);
  }

  private _canAccess(
    route: ActivatedRouteSnapshot | Route,
    moduleId = null,
    entityId = null,
  ):
    | Observable<boolean | UrlTree>
    | Promise<boolean | UrlTree>
    | boolean
    | UrlTree {
    //get the access and the level from the route data
    let access = route.data?.access as EntitlementMode;
    let level = route.data?.level as AccessRightLevelEnum;
    /**
     * When we have a url provided with core module, we change the required level to be AdminAccess, unless
     * the route disable this bypass policy, this will be useful for system editor access to be able to see
     * all the services and entities under it, all requests and entities under it, because the matcher provide the core
     * module on these routes since they are core entities.
     * disableCoreModuleAdminBypassPolicy fix the issue for the editor where he needs to be able to see all services and requests
     * in the requested level
     */
    const disableCoreModuleAdminBypassPolicy =
      route.data?.disableCoreModuleAdminBypassPolicy;
    //If there was no level or no access, this guard is miss used
    if (!access || isNaN(level)) {
      return false;
    }

    //Bypass policies when dealing with core modules/entities
    //Check if this was a module access and the module is core module, modify the access to admin
    if (
      this._doesRequireModuleBypassPolicy(moduleId) &&
      !disableCoreModuleAdminBypassPolicy
    ) {
      access = EntitlementMode.System;
      level = AccessRightLevelEnum.AdminAccess;
    }

    //Pass the current access and level to the helper to get the highest level for this route
    return this.accessHelper
      .getRouteTopAccessLevel({
        access: access,
        moduleId,
        entityId,
      })
      .pipe(
        //Set the current active route access in state
        switchMap((topLevel) => {
          return this.store
            .dispatch(new PermissionActions.SetActiveRouteAccessLevel(topLevel))
            .pipe(mapTo(topLevel));
        }),
        //Replace 1 with the router data level
        map((topLevel) => {
          return topLevel >= level || this.router.parseUrl('403');
        }),
      );
  }

  private _getModuleIdFromRoute(route: ActivatedRouteSnapshot) {
    return route?.paramMap.get('moduleId') ?? '';
  }

  private _getEntityIdFromRoute(route: ActivatedRouteSnapshot) {
    return route?.paramMap.get('entityId') ?? '';
  }

  private _doesRequireModuleBypassPolicy(moduleId: string) {
    return moduleId == CORE_MODULE_ID;
  }
}
